import React, { useEffect } from 'react';
import { useTheme } from '../ThemeContext';

/**
 * Компонент боковой панели для отображения деталей
 * @param {Object} props - Пропсы компонента
 * @param {boolean} props.isOpen - Открыта ли панель
 * @param {Function} props.onClose - Функция закрытия панели
 * @param {string} props.title - Заголовок панели
 * @param {React.ReactNode} [props.titleIcon] - Иконка слева от заголовка (опционально)
 * @param {React.ReactNode} props.children - Содержимое панели
 * @param {React.ReactNode} props.footer - Футер панели (опционально)
 * @param {string} props.width - Ширина панели (по умолчанию 450px)
 * @param {boolean} [props.hideCloseButton] - Скрыть кнопку закрытия (крестик) в шапке
 */
const SidePanel = ({ isOpen, onClose, title, titleIcon, children, footer, width = '450px', hideCloseButton = false }) => {
  const { currentTheme } = useTheme();

  // Обработчик нажатия Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Блокируем прокрутку основной страницы
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Получение классов в зависимости от темы
  const getPanelClass = () => {
    if (currentTheme === 'dark') return 'bg-gray-900 text-white border-l border-gray-700';
    if (currentTheme === 'grither') return 'bg-white border-l border-grither-gray-200';
    if (currentTheme === 'custom') return 'universal-card border-l';
    return 'bg-white border-l border-gray-200';
  };

  const getHeaderClass = () => {
    if (currentTheme === 'dark') return 'bg-gray-800 border-b border-gray-700';
    if (currentTheme === 'grither') return 'bg-grither-gray-50 border-b border-grither-gray-200';
    if (currentTheme === 'custom') return 'border-b';
    return 'bg-gray-50 border-b border-gray-200';
  };

  const getTextClass = (variant = 'primary') => {
    if (currentTheme === 'dark') {
      return variant === 'primary' ? 'text-white' : 'text-gray-400';
    }
    if (currentTheme === 'grither') {
      return variant === 'primary' ? 'text-grither-dark' : 'text-grither-secondary';
    }
    if (currentTheme === 'custom') {
      return variant === 'primary' ? 'universal-text-primary' : 'universal-text-secondary';
    }
    return variant === 'primary' ? 'text-gray-900' : 'text-gray-600';
  };

  return (
    <>
      {/* Overlay - затемнение фона */}
      <div
        className={`side-panel-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
          zIndex: 999,
        }}
      />

      {/* Side Panel */}
      <div
        className={`side-panel ${isOpen ? 'open' : ''} ${getPanelClass()}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: width,
          maxWidth: '100vw',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOpen ? '-4px 0 12px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        {/* Header */}
        <div className={`side-panel-header ${getHeaderClass()} sticky top-0 z-10`}>
          <div className="flex justify-between items-center px-6 py-4">
            <h2 className={`text-xl font-bold flex items-center gap-2 min-w-0 ${getTextClass('primary')}`}>
              {titleIcon && <span className="flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden>{titleIcon}</span>}
              <span className="truncate">{title}</span>
            </h2>
            {!hideCloseButton && (
              <button
                onClick={onClose}
                className={`text-2xl ${getTextClass('secondary')} hover:opacity-70 transition-opacity p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800`}
                aria-label="Закрыть панель"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content - прокручиваемое содержимое */}
        <div 
          className="side-panel-content flex-1 overflow-y-auto px-6 py-6"
          style={{
            scrollbarWidth: 'thin',
          }}
        >
          {children}
        </div>

        {/* Footer - опциональный */}
        {footer && (
          <div className={`side-panel-footer ${getHeaderClass()} sticky bottom-0 px-6 py-4 border-t`}>
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default SidePanel;
